import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ButtonIcon, ButtonLink, Select } from '@resideo/blueprint-oneportal';
import styles from './Profile.module.css';
import { useAuth } from '@/contexts/AuthContext';
import { chevronLeftIcon, externalLinkIcon } from '@/assets/icons';
import { createRisClient } from '@/api/risClient';
import { getApiUrl } from '@/api/apiConfig';
import { getRoleLabel } from '../Employees/roleUtils';
import { StickyFooter } from '../Employees/StickyFooter';
import {
  ClockFormat as ApiClockFormat,
  TemperatureUnit as ApiTemperatureUnit,
} from '@resideo/web-integration-services-api-client';
import type { UpdateUserModel } from '@resideo/web-integration-services-api-client';

type ProfileTab = 'profile' | 'preferences';
type TemperatureUnit = 'fahrenheit' | 'celsius';
type ClockFormat = '12h' | '24h';

interface MyUserProfile {
  firstName?: string | null;
  lastName?: string | null;
  contactEmail?: string | null;
  countryCode?: string | null;
  primaryPhoneNumber?: string | null;
  phoneNumber?: string | null;
  secondaryPhoneNumber?: string | null;
  timeZone?: string | null;
  language?: string | null;
  temperatureUnit?: string | null;
  volumeUnit?: string | null;
  clockFormat?: string | null;
  partnerUsers?: Array<{
    roles?: string[] | null;
    partnerAccount?: { name?: string | null } | null;
  }> | null;
}

interface MyUserApiResponse {
  data?: MyUserProfile | null;
}

const CLOCK_STORAGE_KEY = 'oneportal_pref_clock_format';
const TEMPERATURE_STORAGE_KEY = 'oneportal_pref_temperature_unit';

function readStoredPreference<T extends string>(key: string, fallback: T): T {
  const stored = localStorage.getItem(key);
  return (stored as T) || fallback;
}

export function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, selectedContext, activeRole } = useAuth();
  const [risClient] = useState(() =>
    createRisClient(
      getApiUrl('risPublicApi'),
      () => localStorage.getItem('oneportal_auth_token') ?? localStorage.getItem('authToken')
    )
  );
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');
  const [clockFormat, setClockFormat] = useState<ClockFormat>(() =>
    readStoredPreference(CLOCK_STORAGE_KEY, '12h')
  );
  const [temperatureUnit, setTemperatureUnit] = useState<TemperatureUnit>(() =>
    readStoredPreference(TEMPERATURE_STORAGE_KEY, 'fahrenheit')
  );
  // Last persisted values — the baseline the sticky footer diffs against.
  const [savedPreferences, setSavedPreferences] = useState(() => ({
    temperatureUnit: readStoredPreference<TemperatureUnit>(TEMPERATURE_STORAGE_KEY, 'fahrenheit'),
    clockFormat: readStoredPreference<ClockFormat>(CLOCK_STORAGE_KEY, '12h'),
  }));
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [profile, setProfile] = useState<MyUserProfile | null>(null);

  const currentContext = user?.contexts?.find((c) => c.context === selectedContext);
  const apiPartnerUser = profile?.partnerUsers?.[0];
  const companyName = apiPartnerUser?.partnerAccount?.name
    ?? currentContext?.contextDisplayName
    ?? t('profile.empty', '—');
  const rawRole = apiPartnerUser?.roles?.[0]
    ?? currentContext?.roleName
    ?? activeRole
    ?? '';
  const roleLabel = getRoleLabel(rawRole, t) || t('profile.empty', '\u2014');
  const profileName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ')
    || user?.name
    || t('profile.empty', '—');
  const profileEmail = profile?.contactEmail || user?.email || t('profile.empty', '—');
  const profilePhone = [
    profile?.countryCode,
    profile?.primaryPhoneNumber ?? profile?.phoneNumber,
  ].filter(Boolean).join(' ')
    || t('profile.empty', '—');

  React.useEffect(() => {
    if (!user) return;

    let cancelled = false;
    risClient.myUsers.myUsersGetMyUser()
      .then((response) => {
        if (cancelled) return;
        const apiProfile = (response as MyUserApiResponse | undefined)?.data;
        if (!apiProfile) return;

        setProfile(apiProfile);
        const apiTemperature: TemperatureUnit | null = apiProfile.temperatureUnit
          ? apiProfile.temperatureUnit.toLowerCase() === 'celsius' ? 'celsius' : 'fahrenheit'
          : null;
        const apiClock: ClockFormat | null = apiProfile.clockFormat
          ? apiProfile.clockFormat.toLowerCase().includes('twentyfour') ? '24h' : '12h'
          : null;

        if (apiTemperature) setTemperatureUnit(apiTemperature);
        if (apiClock) setClockFormat(apiClock);
        // Keep the same object when nothing changed so this never re-renders in a loop.
        setSavedPreferences((previous) => {
          const temperature = apiTemperature ?? previous.temperatureUnit;
          const clock = apiClock ?? previous.clockFormat;
          return temperature === previous.temperatureUnit && clock === previous.clockFormat
            ? previous
            : { temperatureUnit: temperature, clockFormat: clock };
        });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [user]);

  const updateProfilePreference = (body: Pick<UpdateUserModel, 'temperatureUnit' | 'clockFormat'>) => {
    return risClient.myUsers.myUsersUpdateMyUserAccount(body);
  };

  const handleClockChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as ClockFormat;
    setClockFormat(value);
  };
  const handleTemperatureChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as TemperatureUnit;
    setTemperatureUnit(value);
  };

  const preferenceChangesCount =
    Number(temperatureUnit !== savedPreferences.temperatureUnit) +
    Number(clockFormat !== savedPreferences.clockFormat);

  const handleCancelPreferences = () => {
    setTemperatureUnit(savedPreferences.temperatureUnit);
    setClockFormat(savedPreferences.clockFormat);
    setActiveTab('profile');
  };

  const handleSavePreferences = async () => {
    setIsSavingPreferences(true);
    try {
      await updateProfilePreference({
        temperatureUnit: temperatureUnit === 'celsius'
          ? ApiTemperatureUnit.CELSIUS
          : ApiTemperatureUnit.FAHRENHEIT,
        clockFormat: clockFormat === '24h'
          ? ApiClockFormat.TWENTY_FOUR_HOUR
          : ApiClockFormat.TWELVE_HOUR,
      });
      localStorage.setItem(TEMPERATURE_STORAGE_KEY, temperatureUnit);
      localStorage.setItem(CLOCK_STORAGE_KEY, clockFormat);
      setSavedPreferences({ temperatureUnit, clockFormat });
      setActiveTab('profile');
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const manageResideoIdUrl = import.meta.env.VITE_RESIDEO_ID_URL ?? '#';

  return (
    <div className={styles.container}>
      <div className={styles.titleBar}>
        <ButtonIcon
          size="small"
          iconType="icon"
          aria-label={t('profile.back')}
          onClick={() => navigate(-1)}
        >
          <img src={chevronLeftIcon} width="20" height="20" alt="" aria-hidden="true" />
        </ButtonIcon>
        <h1 className={styles.title}>{t('profile.title')}</h1>
      </div>
      <div className={styles.body}>
        <nav className={styles.nav} aria-label={t('profile.navLabel')}>
          <Button
            variant="text"
            className={`${styles.navItem} ${activeTab === 'profile' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            {t('profile.tabs.profile')}
          </Button>
          <Button
            variant="text"
            className={`${styles.navItem} ${activeTab === 'preferences' ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab('preferences')}
          >
            {t('profile.tabs.preferences')}
          </Button>
        </nav>

        <div className={styles.content}>
          {activeTab === 'profile' && (
            <>
              <h2 className={styles.name}>{profileName}</h2>
              <hr className={styles.divider} />
              <div className={styles.profileCard}>
              <div className={styles.infoGrid}>
                <div>
                  <h3 className={styles.colHeader}>{t('profile.contact')}</h3>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t('profile.email')}</span>
                    <span className={styles.fieldValue}>{profileEmail}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t('profile.phone')}</span>
                    <span className={styles.fieldValue}>{profilePhone}</span>
                  </div>
                </div>
                <div>
                  <h3 className={styles.colHeader}>{t('profile.company')}</h3>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t('profile.companyLabel')}</span>
                    <span className={styles.fieldValue}>{companyName}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>{t('profile.role')}</span>
                    <span className={styles.fieldValue}>{roleLabel}</span>
                  </div>
                </div>
              </div>
              </div>

              <div className={styles.notice}>
                <p className={styles.noticeText}>
                  {t(
                    'profile.notice'
                  )}
                </p>
                <ButtonLink className={styles.noticeLink} href={manageResideoIdUrl} target="_blank" rel="noopener noreferrer">
                  {t('profile.manageResideoId')}
                  <img src={externalLinkIcon} width="14" height="14" alt="" aria-hidden="true" />
                </ButtonLink>
              </div>
            </>
          )}

          {activeTab === 'preferences' && (
            <>
              <div className={styles.prefRow}>
                <div>
                  <p className={styles.prefTitle}>{t('profile.temperatureFormat')}</p>
                  <p className={styles.prefDesc}>
                    {t('profile.temperatureFormatDesc')}
                  </p>
                </div>
                <div className={styles.prefControl}>
                  <Select
                    name="temperatureUnit"
                    value={temperatureUnit}
                    onChange={handleTemperatureChange}
                    items={[
                      { value: 'fahrenheit', label: t('profile.fahrenheit') },
                      { value: 'celsius', label: t('profile.celsius') },
                    ]}
                  />
                </div>
              </div>

              <div className={styles.prefRow}>
                <div>
                  <p className={styles.prefTitle}>{t('profile.clockControls')}</p>
                  <p className={styles.prefDesc}>
                    {t('profile.clockControlsDesc')}
                  </p>
                </div>
                <div className={styles.prefControl}>
                  <Select
                    name="clockFormat"
                    value={clockFormat}
                    onChange={handleClockChange}
                    items={[
                      { value: '12h', label: t('profile.12Hour') },
                      { value: '24h', label: t('profile.24Hour') },
                    ]}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {activeTab === 'preferences' && (
        <StickyFooter
          changesCount={preferenceChangesCount}
          onCancel={handleCancelPreferences}
          onSave={handleSavePreferences}
          isSaving={isSavingPreferences}
          saveDisabled={preferenceChangesCount === 0}
        />
      )}
    </div>
  );
}

export default Profile;
